import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, MenuItem, TextField } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { BusyDialog } from '../../../comps/BusyDialog';
import { IEntityStore, ISupplier } from '../../../main/entities';
import { transl } from '../../../main/transl';
import { IEntityStateAgg } from '../../../main/useEntityStateAgg';
import { IBooleanStateAgg, useBooleanStateAgg, useStringStateAgg } from '../../../mg/useStateAgg';
import { toasterSvc } from '../../../services/ToasterSvc';
import { useOmnistore } from '../../../stores/Omnistore';
import { SupplierMultiselect } from './SupplierMultiselect';

const useStyles = makeStyles((theme: Theme) => ({
	AutoHarmonizerDialog: {
		'& .MuiDialog-paper': {
			minWidth: 600,
		},
	},
	content: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		alignItems: 'baseline',
		justifyItems: 'stretch',
		gap: '20px',
		'& > div': {
			overflowX: 'hidden',
			paddingLeft: 10,
		},
	},
}));

interface IAutoHarmonizerDialogProps {
	openAgg: IBooleanStateAgg
	afterSuccess(): void
	masterSupplierAgg: IEntityStateAgg<ISupplier, IEntityStore<ISupplier>>
	slaveSupplierAgg: IEntityStateAgg<ISupplier, IEntityStore<ISupplier>>
}

export const AutoHarmonizerDialog = observer(function(props: IAutoHarmonizerDialogProps) {
	const classes = useStyles();
	const {openAgg, afterSuccess, masterSupplierAgg, slaveSupplierAgg} = props;
	const [slaveSupplierIds, setSlaveSupplierIds] = useState(slaveSupplierAgg.value ? [slaveSupplierAgg.id] : []);
	const summaryAgg = useStringStateAgg('');
	const allMasterSuppliersAgg = useBooleanStateAgg();
	const allSlaveSuppliersAgg = useBooleanStateAgg();
	const isBusyAgg = useBooleanStateAgg();
	const omnistore = useOmnistore();
	const communicator = omnistore.communicator;
	function extractSummary(outputList: Array<{text: string}>) : string {
		for (const i of outputList) {
			if (i.text) return i.text;
		}
		return '';
	}
	const execute = () => {
		const masterSupplierId = !allMasterSuppliersAgg.value && masterSupplierAgg.id || null;
		const slaveSupplierIds2 = allSlaveSuppliersAgg.value || !slaveSupplierIds.length ? null : slaveSupplierIds;
		if (!masterSupplierId && !slaveSupplierIds2) {
			toasterSvc.error('someIdRequired');
			return;
		}
		isBusyAgg.trueHandler();
		communicator.doPost('AutoHarmonizer', {masterSupplierId, slaveSupplierIds: slaveSupplierIds2})
			.finally(isBusyAgg.falseHandler)
			.then(toasterSvc.handleResponse('executed'))
			.then(r => {
				if (r.outputList) {
					afterSuccess();
					summaryAgg.setter(extractSummary(r.outputList as any));
				} else {
					openAgg.falseHandler();
				}
			});
	};

	return <>
		<BusyDialog className={classes.AutoHarmonizerDialog} open={openAgg.value && !summaryAgg.value} isBusy={isBusyAgg.value} title={transl('AutoHarmonizerDialog')}>
			<DialogContent className={classes.content}>
				<FormControl>
					<FormControlLabel
						label={transl('allMasterSuppliers')}
						control={<Checkbox name={name} checked={allMasterSuppliersAgg.value} color="default" onChange={allMasterSuppliersAgg.toggleHandler}/>}
					/>
				</FormControl>
				<TextField select
					label={transl('vendor')}
					value={masterSupplierAgg.id}
					onChange={masterSupplierAgg.changeHandler}
					disabled={allMasterSuppliersAgg.value}
				>
					{<MenuItem value={0} key={0}>{transl('harmo_all')}</MenuItem>}
					{masterSupplierAgg.repo.allAsList.map(x => <MenuItem value={x.id} key={x.id}>{x.name}</MenuItem>)}
				</TextField>
				<FormControl>
					<FormControlLabel
						label={transl('allSlaveSuppliers')}
						control={<Checkbox name={name} checked={allSlaveSuppliersAgg.value} color="default" onChange={allSlaveSuppliersAgg.toggleHandler}/>}
					/>
				</FormControl>
				<SupplierMultiselect
					ids={slaveSupplierIds}
					setIds={setSlaveSupplierIds}
					repo={slaveSupplierAgg.repo}
					label={transl('wholesaler')}
					disabled={allSlaveSuppliersAgg.value}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={openAgg.falseHandler}>{transl('cancel')}</Button>
				<Button color="primary" onClick={execute}>{transl('execute')}</Button>
			</DialogActions>
		</BusyDialog>
		<Dialog className={classes.AutoHarmonizerDialog} open={!!summaryAgg.value} onClose={summaryAgg.makeSetter('')}>
			<DialogTitle>{transl('AutoHarmonizerDialogResult')}</DialogTitle>
			<DialogContent className={classes.content}>{summaryAgg.value}</DialogContent>
			<DialogActions>
				<Button onClick={() => {summaryAgg.setter(''); openAgg.falseHandler();}}>{transl('ok')}</Button>
			</DialogActions>
		</Dialog>;
	</>;
});
